import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Volume2,
  Loader2,
  MessageCircle,
  User,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoryView() {
  const { storyId } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [story, setStory] = useState(null);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    fetchStory();
  }, [storyId]);

  const fetchStory = async () => {
    try {
      const response = await axios.get(`${API}/stories/${storyId}`, { withCredentials: true });
      setStory(response.data);
    } catch (error) {
      toast.error('Failed to load story');
    } finally {
      setLoading(false);
    }
  };

  const getText = (scene) => {
    if (language === 'de') return scene.text_de;
    if (language === 'ar') return scene.text_ar;
    return scene.text_en;
  };

  const getTitle = () => {
    if (!story) return '';
    if (language === 'de') return story.title_de;
    if (language === 'ar') return story.title_ar;
    return story.title_en;
  };

  const currentScene = story?.scenes?.[currentSceneIndex];

  const handleChoice = async (option) => {
    if (option.correct === false) {
      toast.error('Try again!');
      return;
    }

    // Move to next scene
    if (currentSceneIndex < story.scenes.length - 1) {
      setCurrentSceneIndex(currentSceneIndex + 1);
    } else {
      // Story complete
      await completeStory();
    }
  };

  const handleContinue = async () => {
    if (currentSceneIndex < story.scenes.length - 1) {
      setCurrentSceneIndex(currentSceneIndex + 1);
    } else {
      await completeStory();
    }
  };

  const completeStory = async () => {
    try {
      const response = await axios.post(`${API}/stories/${storyId}/complete`, {}, { withCredentials: true });
      setCompleted(true);
      toast.success(`Story complete! +${response.data.xp_earned} XP`);
    } catch (error) {
      toast.error('Could not complete story');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="text-center p-8">
          <p className="text-muted-foreground mb-4">Story not found</p>
          <Button onClick={() => navigate('/stories')}>Back to Stories</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="story-view">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/stories')}
              className="text-white hover:bg-white/10"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-heading font-semibold text-white">{getTitle()}</h1>
            <Badge className="bg-amber-500">
              <Zap className="h-4 w-4 mr-1" />
              {story.xp_reward} XP
            </Badge>
          </div>
          <Progress 
            value={((currentSceneIndex + 1) / story.scenes.length) * 100} 
            className="h-2 bg-slate-700"
          />
        </div>
      </header>

      {/* Story Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {completed ? (
          <Card className="bg-slate-800 border-slate-700 text-white text-center py-12">
            <CardContent>
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="font-heading text-2xl font-bold mb-4">Story Complete!</h2>
              <p className="text-white/70 mb-6">You earned {story.xp_reward} XP!</p>
              <div className="flex gap-4 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => { setCurrentSceneIndex(0); setCompleted(false); }}
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Read Again
                </Button>
                <Button 
                  onClick={() => navigate('/stories')}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  More Stories
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Scene */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                {/* Speaker */}
                {currentScene?.speaker && currentScene.speaker !== 'choice' && (
                  <div className="flex items-center gap-2 mb-4">
                    {currentScene.speaker === 'narrator' ? (
                      <Badge variant="outline" className="text-white/70 border-white/30">
                        Narrator
                      </Badge>
                    ) : (
                      <Badge className="bg-teal-500">
                        <MessageCircle className="h-3 w-3 mr-1" />
                        {currentScene.speaker}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Dialogue text */}
                {currentScene?.speaker !== 'choice' && (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xl text-white">{currentScene?.text_de}</p>
                      <AudioButton 
                        text={currentScene?.text_de} 
                        voice="nova"
                        className="text-white/70 hover:text-white"
                      />
                      <MicrophoneButton
                        expectedText={currentScene?.text_de}
                        onPronunciationScore={(score) => {
                          if (score.overall_score >= 70) {
                            toast.success(`Great pronunciation! ${score.overall_score}%`);
                          }
                        }}
                        size="sm"
                        className="text-teal-400"
                      />
                    </div>
                    <p className="text-white/60 italic">{getText(currentScene)}</p>
                  </>
                )}

                {/* Choice options */}
                {currentScene?.speaker === 'choice' && (
                  <div className="space-y-3">
                    <p className="text-white/70 mb-4">Choose your response:</p>
                    {currentScene.options?.map((option, idx) => (
                      <div
                        key={idx}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleChoice(option)}
                        onKeyDown={(e) => e.key === 'Enter' && handleChoice(option)}
                        className="w-full p-4 rounded-xl border-2 border-slate-600 hover:border-teal-500 bg-slate-700/50 hover:bg-slate-700 text-left transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-white font-medium">{option.text_de}</p>
                          <AudioButton 
                            text={option.text_de} 
                            voice="nova"
                            size="sm"
                            className="text-white/70 hover:text-white"
                          />
                        </div>
                        <p className="text-white/60 text-sm">{option.text_en}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Continue button for non-choice scenes */}
            {currentScene?.speaker !== 'choice' && (
              <Button 
                onClick={handleContinue}
                className="w-full py-6 bg-teal-500 hover:bg-teal-600 text-white"
              >
                {currentSceneIndex < story.scenes.length - 1 ? 'Continue' : 'Finish Story'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
